import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import './CloMapping.css';
// import { fetchChannels, fetchLoyaltyTypes, fetchOutletTypes } from '../../services/cloMappingService';
import { fetchDistinctOutletField } from '../../services/cloMappingService';
import { getMetaDataConfig, postConfiguration, validateMetaDataResponse } from '../../services/uiConfigService';
import { getLob } from '../../services/authenticationService';
import { updateConfigRequestBody } from '../../services/managePoliciesService';
import { META_DATA_BATCH_API } from '../../services/networkService';
import { validateWithOtp } from '../../stateManagement/actions/validateOtpPopupActions';
import { manageUpdateAccessObj } from '../manageUpdateAccess/manageUpdateAccessTypes';
import { metaDataBatchPayload } from '../../types/types';
import { GenericPopUp } from '../../components/popup/genericPopUp';
import { popupType } from '../../types/types';
import { defaultCloMappingConfig } from './CloMappingConfig';
import { useSelector } from 'react-redux';
import { ConfirmationPopUp } from '../../components/popup/confirmationPopUp';

const rectangularInputSx = {
  width: '100%',
  height: '50px !important',
  borderRadius: '6px',
  background: '#fafbfc',
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    background: '#fafbfc',
    height: '50px !important',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-select': {
    borderRadius: '6px',
    background: '#fafbfc',
    height: '50px !important',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSvgIcon-root': {
    width: 28,
    height: 28,
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    borderRadius: '6px',
    background: '#fafbfc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px !important', 
    height: '34px !important', 
    display: 'flex',
    alignItems: 'center',
  },
};

const autocompleteSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    background: '#fafbfc',
    minHeight: '50px', 
    '& fieldset': {
      border: '0.5px solid rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      border: '1px solid rgba(0, 0, 0, 0.87)',
    },
    '&.Mui-focused fieldset': {
      border: '1px solid #1976d2',
    },
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiAutocomplete-input': {
    padding: '10px 14px', 
    minHeight: '30px',
    lineHeight: 'normal',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px', 
    minHeight: '30px',
    lineHeight: 'normal',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)',
    right: 12,
  },
};

type Mapping = {
  newOutletType: string;
  channel: string;
  loyaltyType: string;
  outletType: string;
};

export function CloMapping() {
  const [form, setForm] = useState({
    newOutletType: '',
    channel: '',
    loyaltyType: '',
    outletType: '',
  });
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [loyaltyTypes, setLoyaltyTypes] = useState<any[]>([]);
  const [outletTypes, setOutletTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showMainLoader, setShowMainLoader] = useState(false);
  const [showEditLoader, setShowEditLoader] = useState(false);
  const [deleteLoadingIndex, setDeleteLoadingIndex] = useState<number | null>(null);
  const [genericModalState, setGenericModalState] = useState(false);
  const [genericModalMessage, setGenericModalMessage] = useState("");
  const [genericModalType, setGenericModalType] = useState<popupType>("Error");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    newOutletType: '',
    channel: '',
    loyaltyType: '',
    outletType: '',
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
  const otpPopupState = useSelector((state: any) => state.otpPopupState);

  useEffect(() => {
    const loadDropdowns = async () => {
      setLoading(true);
      try {
        // const channelsData = await fetchChannels();
        const channelsData = await fetchDistinctOutletField('channel');
        const filteredChannels = channelsData.filter(
          (item: any) => (item.channel || item.value) && (item.channel || item.value).trim() !== ''
        );
        setChannels(filteredChannels);
      } catch (e) {
        console.error('Error fetching channels:', e);
      }

      try {
        // const loyaltyTypesData = await fetchLoyaltyTypes();
        const loyaltyTypesData = await fetchDistinctOutletField('outletClass');
        const filteredLoyaltyTypes = loyaltyTypesData.filter(
          (item: any) => item.outletClass && item.outletClass.trim() !== ''
        );
        setLoyaltyTypes(filteredLoyaltyTypes);
      } catch (e) {
        console.error('Error fetching loyalty types:', e);
      }

      try {
        // const outletTypesData = await fetchOutletTypes();
        const outletTypesData = await fetchDistinctOutletField('outletType');
        const filteredOutletTypes = outletTypesData.filter(
          (item: any) => (item.outletType || item.value) && (item.outletType || item.value).trim() !== ''
        );
        setOutletTypes(filteredOutletTypes);
      } catch (e) {
        console.error('Error fetching outlet types:', e);
      }

      try {
        await loadExistingMappings();
      } catch (e) {
        console.error('Error loading existing mappings:', e);
      }
      setLoading(false);
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (!otpPopupState.isOpen) {
      setDeleteLoadingIndex(null);
    }
  }, [otpPopupState.isOpen]);

  const loadExistingMappings = async () => {
    try {
      const clientConfig = await getMetaDataConfig("clientconfig");
      const cloMappingConfig = clientConfig.find((config: { domainType: string }) => 
        config.domainType === "clo_mapping"
      );
      if (cloMappingConfig?.domainValues?.[0]?.value) {
        setMappings(cloMappingConfig.domainValues[0].value);
      } else {
        setMappings(defaultCloMappingConfig[0].value);
      }
    } catch (error) {
      console.error('Error loading existing mappings:', error);
      setMappings(defaultCloMappingConfig[0].value);
    }
  };

  const openPopUp = (message: string, modalType: popupType) => {
    setGenericModalMessage(message);
    setGenericModalState(true);
    setGenericModalType(modalType);
  };

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMap = async (e: React.FormEvent) => {
    e.preventDefault();
    // Check if all fields are filled
    if (!form.newOutletType || !form.channel || !form.loyaltyType || !form.outletType) {
      openPopUp("All fields are required to fill.", "Error");
      return;
    }
    // 1. Check for duplicate newOutletType (case-insensitive)
    const isDuplicateOutletType = mappings.some(
      (m) => (m.newOutletType ?? '').trim().toLowerCase() === (form.newOutletType ?? '').trim().toLowerCase()
    );
    if (isDuplicateOutletType) {
      openPopUp("This Store Type already exists!", "Error");
      return;
    }
    // 2. Check for duplicate combination of outletType, loyaltyType, and channel
    const isDuplicateCombo = mappings.some(
      (m) =>
        m.outletType === form.outletType &&
        m.loyaltyType === form.loyaltyType &&
        m.channel === form.channel
    );
    if (isDuplicateCombo) {
      openPopUp("This Outlet Type, Loyalty Type, and Channel combination already exists!", "Error");
      return;
    }
    if (
      form.newOutletType &&
      form.channel &&
      form.loyaltyType &&
      form.outletType
    ) {
      const newMapping = {
        newOutletType: form.newOutletType,
        channel: form.channel,
        loyaltyType: form.loyaltyType,
        outletType: form.outletType,
      };
      const updatedMappings = [...mappings, newMapping];
      const cloMappingPayload: updateConfigRequestBody = {
        domainName: "clientconfig",
        domainType: "clo_mapping",
        domainValues: [
          {
            ...defaultCloMappingConfig[0],
            value: updatedMappings
          }
        ],
        lob: getLob(),
      };
      const finalMetaDataPayload: metaDataBatchPayload = { 
        features: [cloMappingPayload] 
      };
      validateWithOtp(
        async (verifiedUser?: manageUpdateAccessObj, verifyResponse?: any) => {
          if (verifyResponse && verifyResponse.status >= 200 && verifyResponse.status < 300) {
            setShowMainLoader(true);
            setMappings(updatedMappings);
            setForm({ newOutletType: '', channel: '', loyaltyType: '', outletType: '' });
            await doFinalSubmit(verifiedUser, verifyResponse, 'main', 'add');
            setShowMainLoader(false);
          }
        },
        undefined,
        "all",
        META_DATA_BATCH_API,
        finalMetaDataPayload,
        "PUT"
      );
    }
  };

  const doFinalSubmit = async (verifiedUser?: manageUpdateAccessObj, verifyResponse?: any, loaderType?: 'main' | 'edit', operationType: 'add' | 'edit' | 'delete' = 'edit') => {
    try {
      if (verifyResponse.status >= 200 && verifyResponse.status < 300) {
        const validationObj = validateMetaDataResponse(verifyResponse?.data?.features);
        if (validationObj.success) {
          if (operationType === 'delete') {
            openPopUp("Mapping deleted successfully!", "Success");
          } else {
            openPopUp("Mapping saved successfully!", "Success");
          }
        } else {
          openPopUp(validationObj.message, "Error");
        }
      } else {
        throw new Error();
      }
    } catch (error) {
      openPopUp("Something went wrong while saving mapping data", "Error");
    } finally {
      if (loaderType === 'main') setShowMainLoader(false);
      if (loaderType === 'edit') setShowEditLoader(false);
    }
  };

  const handleDelete = async (index: number) => {
    setDeleteLoadingIndex(index);
    const updatedMappings = mappings.filter((_, i) => i !== index);
    const cloMappingPayload: updateConfigRequestBody = {
      domainName: "clientconfig",
      domainType: "clo_mapping",
      domainValues: [
        {
          ...defaultCloMappingConfig[0],
          value: updatedMappings
        }
      ],
      lob: getLob(),
    };
    const finalMetaDataPayload: metaDataBatchPayload = { 
      features: [cloMappingPayload] 
    };
    validateWithOtp(
      async (verifiedUser?: manageUpdateAccessObj, verifyResponse?: any) => {
        if (verifyResponse && verifyResponse.status >= 200 && verifyResponse.status < 300) {
          setMappings(updatedMappings);
          await doFinalSubmit(verifiedUser, verifyResponse, 'main', 'delete');
        }
        setDeleteLoadingIndex(null);
      },
      undefined,
      "all",
      META_DATA_BATCH_API,
      finalMetaDataPayload,
      "PUT"
    );
  };

  const handleEdit = (index: number) => {
    const mapping = mappings[index];
    setEditForm({
      newOutletType: mapping.newOutletType,
      channel: mapping.channel,
      loyaltyType: mapping.loyaltyType,
      outletType: mapping.outletType,
    });
    setEditingIndex(index);
    setEditModalOpen(true);
  };

  const handleEditChange = (field: string, value: string) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditSave = async () => {
    if (editingIndex !== null) {
      const original = mappings[editingIndex];
      
      // Validate that all fields are filled
      if (!(editForm.newOutletType ?? '').trim() || !(editForm.channel ?? '').trim() || !(editForm.loyaltyType ?? '').trim() || !(editForm.outletType ?? '').trim()) {
        openPopUp("All fields are required. Please fill in all fields.", "Error");
        // Revert to original values
        setEditForm({
          newOutletType: original.newOutletType,
          channel: original.channel,
          loyaltyType: original.loyaltyType,
          outletType: original.outletType,
        });
        return;
      }
      
      const isChanged =
        editForm.newOutletType !== original.newOutletType ||
        editForm.channel !== original.channel ||
        editForm.loyaltyType !== original.loyaltyType ||
        editForm.outletType !== original.outletType;

      if (!isChanged) {
        openPopUp("Please change at least one field to update.", "Error");
        return;
      }

      const isDuplicateOutletType = mappings.some((m, idx) =>
          idx !== editingIndex &&
          (m.newOutletType ?? '').trim().toLowerCase() === (editForm.newOutletType ?? '').trim().toLowerCase()
        );
      if (isDuplicateOutletType) {
        openPopUp("This Store Type already exists!", "Error");
        return;
      }

      const isDuplicateCombo = mappings.some((m, idx) =>
        idx !== editingIndex &&
        m.outletType === editForm.outletType &&
        m.loyaltyType === editForm.loyaltyType &&
        m.channel === editForm.channel
      );
      if (isDuplicateCombo) {
        openPopUp("This Outlet Type, Loyalty Type, and Channel combination already exists!", "Error");
        return;
      }

      const updatedMappings = [...mappings];
      updatedMappings[editingIndex] = {
        ...original,
        ...editForm,
      };
      setEditModalOpen(false);
      setEditingIndex(null);
      setEditForm({
        newOutletType: '',
        channel: '',
        loyaltyType: '',
        outletType: '',
      });

      const cloMappingPayload = {
        domainName: "clientconfig",
        domainType: "clo_mapping",
        domainValues: [
          {
            ...defaultCloMappingConfig[0],
            value: updatedMappings
          }
        ],
        lob: getLob(),
      };
      const finalMetaDataPayload = { features: [cloMappingPayload] };
      validateWithOtp(
        async (verifiedUser, verifyResponse) => {
          if (verifyResponse && verifyResponse.status >= 200 && verifyResponse.status < 300) {
            setShowEditLoader(true);
            setMappings(updatedMappings);
            setShowEditLoader(false);
            await doFinalSubmit(verifiedUser, verifyResponse, 'edit', 'edit');
          }
        },
        undefined,
        "all",
        META_DATA_BATCH_API,
        finalMetaDataPayload,
        "PUT"
      );
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingIndex(null);
    setEditForm({
      newOutletType: '',
      channel: '',
      loyaltyType: '',
      outletType: '',
    });
  };

  return (
    <Box className="clo-mapping-root">
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <Box sx={{ borderRadius: 2, px: { xs: 2, md: 4 }, py: 2, mb: 3, mt: { xs: 1, md: 2 }, position: 'relative', zIndex: 1, background: 'transparent', boxShadow: 'none' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#23272f',
                fontSize: { xs: 24, md: 28 },
                m: 0
              }}
            >
              Map Outlet Type to COL
            </Typography>
          </Box>

          <Paper elevation={0} className="clo-mapping-form-card" sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
            <form onSubmit={handleMap}>
              <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#23272f', fontSize: 16 }}>
                    Store Type
                  </Typography>
                  <TextField
                    label=""
                    placeholder="e.g., Pan Plus, Grocery"
                    value={form.newOutletType}
                    onChange={(e) => handleChange('newOutletType', e.target.value)}
                    size="small"
                    sx={rectangularInputSx}
                  />
                </Grid>
                <Grid item xs={12} md={2.5}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#23272f', fontSize: 16 }}>
                    Channel
                  </Typography>
                  <Autocomplete
                    options={channels}
                    getOptionLabel={(option) => option.channel || option.value || ''}
                    value={channels.find(ch => (ch.channel || ch.value) === form.channel) || null}
                    onChange={(event, newValue) => {
                      handleChange('channel', newValue ? (newValue.channel || newValue.value) : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Channel"
                        size="small"
                      />
                    )}
                    sx={autocompleteSx}
                    noOptionsText="No channels available"
                  />
                </Grid>
                <Grid item xs={12} md={2.5}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#23272f', fontSize: 16 }}>
                    Loyalty Type
                  </Typography>
                  <Autocomplete
                    options={loyaltyTypes}
                    getOptionLabel={(option) => option.outletClass || ''}
                    value={loyaltyTypes.find(lt => lt.outletClass === form.loyaltyType) || null}
                    onChange={(event, newValue) => {
                      handleChange('loyaltyType', newValue ? newValue.outletClass : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Loyalty type"
                        size="small"
                      />
                    )}
                    sx={autocompleteSx}
                    noOptionsText="No loyalty types available"
                  />
                </Grid>
                <Grid item xs={12} md={2.5}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#23272f', fontSize: 16 }}>
                    Outlet Type
                  </Typography>
                  <Autocomplete
                    options={outletTypes}
                    getOptionLabel={(option) => option.outletType || option.value || ''}
                    value={outletTypes.find(ot => (ot.outletType || ot.value) === form.outletType) || null}
                    onChange={(event, newValue) => {
                      handleChange('outletType', newValue ? (newValue.outletType || newValue.value) : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Outlet type"
                        size="small"
                      />
                    )}
                    sx={autocompleteSx}
                    noOptionsText="No outlet types available"
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={showMainLoader}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: 20,
                      height: 52,
                      width: { xs: '100%', md: '100px' },
                      background: '#24c6b1',
                      textTransform: 'none',
                      boxShadow: 'none',
                      p: 0,
                      maxWidth: '100%',
                      '&:hover': { background: '#1fae9e' },
                    }}
                  >
                    {showMainLoader ? <CircularProgress size={20} color="inherit" /> : 'Map'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>

          <Paper elevation={0} className="clo-mapping-table-card" sx={{ p: { xs: 2, md: 3 } }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: '#23272f', mb: 2, fontSize: 24 }}
            >
              Existing Mappings
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f7f8fa' }}>
                    <TableCell className="clo-mapping-th" sx={{ fontWeight: 700, color: '#23272f', fontSize: 16, letterSpacing: 1 }}>
                     STORE TYPE
                    </TableCell>
                    <TableCell className="clo-mapping-th" sx={{ fontWeight: 700, color: '#23272f', fontSize: 16, letterSpacing: 1 }}>
                      CHANNEL
                    </TableCell>
                    <TableCell className="clo-mapping-th" sx={{ fontWeight: 700, color: '#23272f', fontSize: 16, letterSpacing: 1 }}>
                      LOYALTY TYPE
                    </TableCell>
                    <TableCell className="clo-mapping-th" sx={{ fontWeight: 700, color: '#23272f', fontSize: 16, letterSpacing: 1 }}>
                      OUTLET TYPE
                    </TableCell>
                    <TableCell className="clo-mapping-th" align="center" sx={{ fontWeight: 700, color: '#23272f', fontSize: 16, letterSpacing: 1 }}>
                      ACTIONS
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((row, idx) => (
                    <TableRow
                      key={idx}
                      sx={{ background: '#fff' }}
                    >
                      <TableCell sx={{ fontWeight: 500, fontSize: 16, color: '#23272f' }}>{row.newOutletType}</TableCell>
                      <TableCell sx={{ fontSize: 16, color: '#666666' }}>{row.channel}</TableCell>
                      <TableCell sx={{ fontSize: 16, color: '#666666' }}>{row.loyaltyType}</TableCell>
                      <TableCell sx={{ fontSize: 16, color: '#666666' }}>{row.outletType}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton 
                            color="primary" 
                            size="small" 
                            sx={{ p: 0.5 }}
                            onClick={() => handleEdit(idx)}
                            disabled={showMainLoader || showEditLoader}
                          >
                            <EditIcon sx={{ color: '#1976d2' }} />
                          </IconButton>
                          <IconButton 
                            color="error" 
                            size="small" 
                            sx={{ p: 0.5 }} 
                            onClick={() => {
                              setPendingDeleteIndex(idx);
                              setDeleteConfirmOpen(true);
                            }}
                            disabled={deleteLoadingIndex === idx || showEditLoader}
                          >
                            <DeleteIcon sx={{ color: '#e53935' }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      <GenericPopUp
        type={genericModalType}
        message={genericModalMessage}
        openGenericModal={genericModalState}
        setOpenGenericModal={() => {
          setGenericModalState(false);
        }}
      />

      {/* Edit Modal */}
      {editModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
          onClick={handleEditCancel}
        >
          <Paper
            elevation={3}
            sx={{
              p: 3,
              width: { xs: '90%', md: '600px' },
              maxHeight: '80vh',
              overflow: 'auto',
              borderRadius: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#23272f',
                mb: 3,
                textAlign: 'center'
              }}
            >
              Edit Mapping
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#23272f', fontSize: 16 }}>
                  Store Type
                </Typography>
                <TextField
                  label=""
                  placeholder="e.g., Pan Plus, Grocery"
                  value={editForm.newOutletType}
                  onChange={(e) => handleEditChange('newOutletType', e.target.value)}
                  size="small"
                  sx={rectangularInputSx}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#23272f', fontSize: 16 }}>
                  Channel
                </Typography>
                <Autocomplete
                  options={channels || []}
                  getOptionLabel={(option) => option.channel || option.value || ''}
                  value={(channels || []).find(ch => (ch.channel || ch.value) === editForm.channel) || null}
                  onChange={(event, newValue) => {
                    handleEditChange('channel', newValue ? (newValue.channel || newValue.value) : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Channel"
                      size="small"
                    />
                  )}
                  sx={autocompleteSx}
                  noOptionsText="No channels available"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#23272f', fontSize: 16 }}>
                  Loyalty Type
                </Typography>
                <Autocomplete
                  options={loyaltyTypes || []}
                  getOptionLabel={(option) => option.outletClass || ''}
                  value={(loyaltyTypes || []).find(lt => lt.outletClass === editForm.loyaltyType) || null}
                  onChange={(event, newValue) => {
                    handleEditChange('loyaltyType', newValue ? newValue.outletClass : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Loyalty type"
                      size="small"
                    />
                  )}
                  sx={autocompleteSx}
                  noOptionsText="No loyalty types available"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#23272f', fontSize: 16 }}>
                  Outlet Type
                </Typography>
                <Autocomplete
                  options={outletTypes || []}
                  getOptionLabel={(option) => option.outletType || option.value || ''}
                  value={(outletTypes || []).find(ot => (ot.outletType || ot.value) === editForm.outletType) || null}
                  onChange={(event, newValue) => {
                    handleEditChange('outletType', newValue ? (newValue.outletType || newValue.value) : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Outlet type"
                      size="small"
                    />
                  )}
                  sx={autocompleteSx}
                  noOptionsText="No outlet types available"
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleEditCancel}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: 16,
                    height: 48,
                    width: 120,
                    textTransform: 'none',
                    borderColor: '#ccc',
                    color: '#666',
                    '&:hover': { borderColor: '#999', backgroundColor: '#f5f5f5' },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleEditSave}
                  disabled={showEditLoader}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: 16,
                    height: 48,
                    width: 120,
                    background: '#24c6b1',
                    textTransform: 'none',
                    boxShadow: 'none',
                    '&:hover': { background: '#1fae9e' },
                  }}
                >
                  {showEditLoader ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      <ConfirmationPopUp
        openConfirmModal={deleteConfirmOpen}
        setOpenConfirmModal={setDeleteConfirmOpen}
        message="Are you sure you want to delete this mapping?"
        successMethod={() => {
          if (pendingDeleteIndex !== null) {
            handleDelete(pendingDeleteIndex);
          }
          setPendingDeleteIndex(null);
        }}
        rejectMethod={() => {
          setPendingDeleteIndex(null);
        }}
      />
    </Box>
  );
} 